/**
 * Diálogo de acto médico del módulo Colaboradores (2026-08-25).
 * Lo usan la página de Colaboradores y el bloque de acto médico del episodio
 * (ahí con `fijo`: paciente, fecha y episodio clavados).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Banknote, Loader2, Stethoscope, Syringe } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const eur = (v) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');
const hoyISO = () => new Date().toISOString().slice(0, 10);

export default function ColabActividadDialog({ tipo, onClose, catalogos, medicos, esGestion, medicoActual, onCreada, fijo = null }) {
  const [f, setF] = useState({});
  const [guardando, setGuardando] = useState(false);
  useEffect(() => {
    if (!tipo) return;
    // `fijo` viene del episodio de la historia: paciente, fecha y episodio
    // quedan clavados (2026-08-25: que vaya con cada episodio).
    setF({ fecha: fijo?.fecha || hoyISO(), medico_id: medicoActual?.medico_id || '',
           paciente_nombre: fijo?.paciente_nombre || '', patient_id: fijo?.patient_id,
           episodio_id: fijo?.episodio_id,
           // Varias líneas de producto en un tratamiento (2026-08-27: en una
           // misma zona se pueden poner dos productos distintos).
           lineas: [{ producto: '', cantidad: '' }] });
  }, [tipo, medicoActual, fijo]);
  if (!tipo) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const tratamientoSel = (catalogos?.tratamientos || []).find((t) => t.nombre === f.tratamiento);
  const lineas = f.lineas || [{ producto: '', cantidad: '' }];
  const esRevision = lineas[0]?.producto === '__REVISION__';
  const setLinea = (i, k, v) => setF((prev) => ({
    ...prev,
    lineas: (prev.lineas || []).map((l, idx) => (idx === i ? { ...l, [k]: v } : l)),
  }));
  const addLinea = () => setF((prev) => ({ ...prev, lineas: [...(prev.lineas || []), { producto: '', cantidad: '' }] }));
  const quitarLinea = (i) => setF((prev) => ({
    ...prev,
    lineas: (prev.lineas || []).filter((_, idx) => idx !== i),
  }));
  const lineasValidas = lineas.filter((l) => l.producto && l.producto !== '__REVISION__');
  const costeLineas = lineasValidas.reduce((acc, l) => {
    const prod = (catalogos?.productos || []).find((pp) => pp.nombre === l.producto);
    return acc + (prod ? Number(prod.coste_vial || 0) * (parseFloat(String(l.cantidad).replace(',', '.')) || 1) : 0);
  }, 0);

  const guardar = async () => {
    setGuardando(true);
    try {
      const body = { tipo, ...f, medico_id: f.medico_id || undefined };
      delete body.lineas;
      if (tipo === 'tratamiento') {
        if (esRevision) {
          body.revision_sin_tratamiento = true;
        } else {
          body.productos = lineasValidas.map((l) => ({
            producto: l.producto,
            cantidad: parseFloat(String(l.cantidad).replace(',', '.')) || 1,
          }));
        }
      }
      const r = await api.post('/api/colaboradores/actividades', body);
      toast.success(`Anotado — su parte: ${eur(r.data?.parte_medico)}`);
      onCreada();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally { setGuardando(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[88dvh] overflow-y-auto" data-testid="colab-dialogo">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {tipo === 'tratamiento' ? <Syringe className="w-5 h-5 text-[#2E6E5E]" /> : <Stethoscope className="w-5 h-5 text-[#2E6E5E]" />}
            {tipo === 'tratamiento' ? 'Nuevo tratamiento' : 'Nueva cirugía'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {esGestion && (
            <div>
              <Label>Médico</Label>
              <Select value={f.medico_id || ''} onValueChange={(v) => set('medico_id', v)}>
                <SelectTrigger data-testid="colab-medico-select"><SelectValue placeholder="Elige médico…" /></SelectTrigger>
                <SelectContent>
                  {medicos.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={f.fecha || ''} onChange={(e) => set('fecha', e.target.value)} disabled={!!fijo} data-testid="colab-fecha" />
            </div>
            <div>
              <Label>Paciente</Label>
              <Input value={f.paciente_nombre || ''} onChange={(e) => set('paciente_nombre', e.target.value)}
                placeholder="Nombre del paciente" disabled={!!fijo} data-testid="colab-paciente" />
            </div>
          </div>

          {tipo === 'tratamiento' ? (
            <>
              <div>
                <Label>Tratamiento</Label>
                <Select value={f.tratamiento || ''} onValueChange={(v) => set('tratamiento', v)}>
                  <SelectTrigger data-testid="colab-tratamiento"><SelectValue placeholder="Del catálogo…" /></SelectTrigger>
                  <SelectContent>
                    {(catalogos?.tratamientos || []).map((t) => (
                      <SelectItem key={t.id} value={t.nombre}>{t.nombre} — {eur(t.pvp)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Productos usados <span className="text-red-600">*</span></Label>
                {lineas.map((linea, i) => (
                  <div key={i} className={`grid ${esRevision ? 'grid-cols-1' : 'grid-cols-[1fr_92px_32px]'} gap-2 mt-1.5 items-center`}>
                    <Select value={linea.producto || ''} onValueChange={(v) => {
                      if (i === 0 && v === '__REVISION__') {
                        setF((prev) => ({ ...prev, lineas: [{ producto: '__REVISION__', cantidad: '' }], revision_sin_tratamiento: true }));
                      } else {
                        setLinea(i, 'producto', v);
                        if (i === 0) set('revision_sin_tratamiento', false);
                      }
                    }}>
                      <SelectTrigger data-testid={`colab-producto-${i}`}><SelectValue placeholder="Del catálogo… (obligatorio)" /></SelectTrigger>
                      <SelectContent>
                        {i === 0 && (
                          <SelectItem value="__REVISION__">— REVISIÓN SIN TRATAMIENTO (no se aplicó producto) —</SelectItem>
                        )}
                        {(catalogos?.productos || []).map((p) => (
                          <SelectItem key={p.id} value={p.nombre}>{p.nombre} — {eur(p.coste_vial)}/vial</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!esRevision && (
                      <Input inputMode="decimal" value={linea.cantidad ?? ''} onChange={(e) => setLinea(i, 'cantidad', e.target.value)}
                        placeholder="viales" title="Cantidad (viales)" data-testid={`colab-cantidad-${i}`} />
                    )}
                    {!esRevision && (
                      lineas.length > 1 ? (
                        <button type="button" onClick={() => quitarLinea(i)}
                          className="text-muted-foreground hover:text-destructive text-lg leading-none"
                          title="Quitar esta línea" data-testid={`colab-quitar-linea-${i}`}>×</button>
                      ) : <span />
                    )}
                  </div>
                ))}
                {!esRevision && (
                  <Button type="button" size="sm" variant="outline" className="mt-2 h-8 text-xs"
                    onClick={addLinea} data-testid="colab-anadir-producto">
                    + Añadir otro producto
                  </Button>
                )}
              </div>
              {esRevision && (
                <p className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1.5">
                  Se registrará la visita como <strong>revisión sin tratamiento</strong>, con importes a cero.
                </p>
              )}
              {!esRevision && (
                <div>
                  <Label>Descuento % <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                  <Input inputMode="decimal" value={f.descuento_pct ?? ''} onChange={(e) => set('descuento_pct', e.target.value)}
                    placeholder="0" data-testid="colab-descuento" />
                </div>
              )}
              {tratamientoSel && !esRevision && lineasValidas.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  PVP fijo {eur(tratamientoSel.pvp)} · productos {eur(costeLineas)} — el resto lo calcula el sistema.
                </p>
              )}
            </>
          ) : (
            <>
              <div>
                <Label>Intervención <span className="text-red-600">*</span></Label>
                <Select value={f.intervencion || ''} onValueChange={(v) => {
                  const c = (catalogos?.cirugias || []).find((x) => x.nombre === v);
                  setF((prev) => ({
                    ...prev, intervencion: v,
                    // El sugerido solo rellena la casilla: el precio final lo
                    // escribe el cirujano (2026-08-25).
                    precio_con_iva: c && !c.sin_iva ? c.sugerido : '',
                    precio_sin_iva: c && c.sin_iva ? c.sugerido : '',
                  }));
                }}>
                  <SelectTrigger data-testid="colab-intervencion"><SelectValue placeholder="Del catálogo… (obligatorio)" /></SelectTrigger>
                  <SelectContent>
                    {(catalogos?.cirugias || []).map((c) => (
                      <SelectItem key={c.id} value={c.nombre}>
                        {c.nombre} — sugerido {eur(c.sugerido)}{c.sin_iva ? ' (sin IVA)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">El sugerido es orientativo: escribe debajo el precio que consideres.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Precio (con IVA)</Label>
                  <Input inputMode="decimal" value={f.precio_con_iva ?? ''} onChange={(e) => set('precio_con_iva', e.target.value)}
                    placeholder="0,00" data-testid="colab-precio" />
                </div>
                <div>
                  <Label>o precio SIN IVA</Label>
                  <Input inputMode="decimal" value={f.precio_sin_iva ?? ''} onChange={(e) => set('precio_sin_iva', e.target.value)}
                    placeholder="si es exenta" data-testid="colab-precio-siniva" />
                </div>
                <div>
                  <Label>Anestesia</Label>
                  <Input inputMode="decimal" value={f.anestesia ?? ''} onChange={(e) => set('anestesia', e.target.value)} placeholder="0,00" />
                </div>
                <div>
                  <Label>Instrumentista</Label>
                  <Input inputMode="decimal" value={f.instrumentista ?? ''} onChange={(e) => set('instrumentista', e.target.value)} placeholder="0,00" />
                </div>
                <div>
                  <Label>Fungibles</Label>
                  <Input inputMode="decimal" value={f.fungibles ?? ''} onChange={(e) => set('fungibles', e.target.value)}
                    placeholder={catalogos?.config?.fungibles_defecto != null
                      ? `${catalogos.config.fungibles_defecto} (fijo)` : '0,00'} />
                </div>
                <div>
                  <Label>Coste producto <span className="text-muted-foreground text-xs">(si lo hay)</span></Label>
                  <Input inputMode="decimal" value={f.coste_producto ?? ''} onChange={(e) => set('coste_producto', e.target.value)} placeholder="0,00" />
                </div>
              </div>
            </>
          )}

          <Button onClick={guardar}
            disabled={guardando || !f.paciente_nombre?.trim() || (tipo === 'tratamiento'
              ? (esRevision ? false : !(f.tratamiento && lineasValidas.length > 0
                  && lineasValidas.every((l) => String(l.cantidad ?? '').trim())))
              : !(f.intervencion && (String(f.precio_con_iva ?? '').trim() || String(f.precio_sin_iva ?? '').trim())))}
            className="w-full bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="colab-guardar">
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Banknote className="w-4 h-4 mr-2" />}
            Guardar — el sistema calcula el resto
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
